import classNames from "classnames";
import React, { FC, useMemo } from "react";

import Button from "../../../components/button/Button";
import CustomSelect from "../../../components/select/Select";
import { TimePeriod } from "../../../hooks/useDetectTimePeriod";
import { SelectOptionType } from "../../../types/types";
import { Period, SelectDate } from "../SelectDate";

export type InvoiceFilterField =
  | "status"
  | "assignee"
  | "client"
  | "invoice-date"
  | "label";

type Props = {
  showFilters: InvoiceFilterField[];

  filter: Period;
  setFilter: React.Dispatch<React.SetStateAction<Period>>;

  filterByStatus: SelectOptionType[];
  setFilterByStatus: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;

  filterByUser: SelectOptionType[];
  setFilterByUser: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;

  filterByContact: SelectOptionType[];
  setFilterByContact: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;

  filterByLabel: SelectOptionType[];
  setFilterByLabel: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;

  usersOptions?: SelectOptionType[];
  clientOptions?: SelectOptionType[];
  labelOptions?: SelectOptionType[];

  isLoadingUsers?: boolean;
  isLoadingContacts?: boolean;

  searchClients?: string;
  setSearchClients?: (value: string) => void;

  className?: string;
  defaultTimePeriod?: TimePeriod;
  /** Billing list look (V49 design). */
  billing?: boolean;
};

const STATUS_OPTIONS: SelectOptionType[] = [
  { value: "NotDueYet", label: "Not due yet" },
  { value: "OverDue", label: "Overdue" },
  { value: "PartiallyPaid", label: "Partially paid" },
  { value: "Paid", label: "Paid" },
];

/** BILL-016 (QA batch 17): "All" = no assignee picked (shown as chosen then). */
export const ALL_ASSIGNEES: SelectOptionType = { value: "__all__", label: "All" };

/**
 * Picking "All" clears the people; picking a person drops "All"; removing the
 * last person goes back to All.
 */
export const assigneeFilterAfterPick = (
  before: SelectOptionType[],
  picked: SelectOptionType[] | null | undefined,
): SelectOptionType[] => {
  const list = picked || [];
  const hadAll = !before.length;
  const pickedAll = list.some((item) => item.value === ALL_ASSIGNEES.value);
  const people = list.filter((item) => item.value !== ALL_ASSIGNEES.value);
  if (pickedAll && !hadAll) return [];
  return people;
};

export const InvoiceFiltersBase: FC<Props> = ({
  showFilters,
  filter,
  setFilter,
  filterByStatus,
  setFilterByStatus,
  filterByUser,
  setFilterByUser,
  filterByContact,
  setFilterByContact,
  filterByLabel,
  setFilterByLabel,
  usersOptions = [],
  clientOptions = [],
  labelOptions = [],
  isLoadingUsers = false,
  isLoadingContacts = false,
  searchClients = "",
  setSearchClients,
  className,
  defaultTimePeriod = TimePeriod.All,
  billing = false,
}) => {
  const labelClass = billing
    ? "mb-1.5 text-[12.5px] font-bold normal-case text-[#3A4256]"
    : "mb-1 text-sm font-semibold normal-case text-gray-800";
  const widthClass = (billingWidth: string) =>
    billing ? billingWidth : "min-w-[200px]";
  const selectStyle = { small: true, billing };
  const isShowClear = useMemo(() => {
    return (
      !!filterByStatus.length ||
      !!filterByUser.length ||
      !!filterByContact.length ||
      !!filterByLabel.length ||
      filter.period !== defaultTimePeriod
    );
  }, [
    defaultTimePeriod,
    filter.period,
    filterByStatus,
    filterByUser,
    filterByContact,
    filterByLabel,
  ]);

  return (
    <div
      className={classNames(
        "flex flex-wrap items-end",
        billing ? "gap-3.5" : "gap-4",
        className,
      )}>
      {showFilters.includes("status") && (
        <div>
          <div className={labelClass}>Status</div>
          <div className={widthClass("w-[150px]")}>
            <CustomSelect
              placeholder={"Search status"}
              value={filterByStatus}
              isMulti
              withMenu
              options={STATUS_OPTIONS}
              onChange={(newValue) =>
                setFilterByStatus(newValue as SelectOptionType[])
              }
              selectStyle={{ ...selectStyle, fitChips: true }}
            />
          </div>
        </div>
      )}

      {showFilters.includes("assignee") && (
        <div>
          <div className={labelClass}>Assignee</div>
          <div className={widthClass("w-[150px]")}>
            <CustomSelect
              placeholder={"Search user"}
              value={filterByUser.length ? filterByUser : [ALL_ASSIGNEES]}
              isMulti
              options={[ALL_ASSIGNEES, ...usersOptions]}
              withMenu
              isLoading={isLoadingUsers}
              onChange={(newValue) =>
                setFilterByUser(
                  assigneeFilterAfterPick(
                    filterByUser,
                    newValue as SelectOptionType[],
                  ),
                )
              }
              selectStyle={{ ...selectStyle, fitChips: true }}
            />
          </div>
        </div>
      )}

      {showFilters.includes("client") && (
        <div>
          <div className={labelClass}>Client</div>
          <div className={widthClass("w-[160px]")}>
            <CustomSelect
              placeholder={"Search client"}
              value={filterByContact}
              isMulti
              inputValue={searchClients}
              options={clientOptions}
              isLoading={isLoadingContacts}
              onChangeInput={(inputValue) => setSearchClients?.(inputValue)}
              onChange={(newValue) =>
                setFilterByContact(newValue as SelectOptionType[])
              }
              selectStyle={{ ...selectStyle, countLabel: "clients" }}
            />
          </div>
        </div>
      )}

      {showFilters.includes("invoice-date") && (
        <div>
          <div className={labelClass}>Invoice Date</div>
          <div className={widthClass("w-[140px]")}>
            <SelectDate
              setValue={setFilter}
              value={filter}
              small
              billing={billing}
            />
          </div>
        </div>
      )}

      {showFilters.includes("label") && (
        <div>
          <div className={labelClass}>Label</div>
          <div className={widthClass("w-[150px]")}>
            <CustomSelect
              placeholder={"Search label"}
              value={filterByLabel}
              isMulti
              withMenu
              options={labelOptions}
              onChange={(newValue) =>
                setFilterByLabel(newValue as SelectOptionType[])
              }
              selectStyle={{ ...selectStyle, fitChips: true }}
            />
          </div>
        </div>
      )}

      {isShowClear && (
        <div>
          <div className={"h-6"} />
          <Button
            buttonType={"button"}
            colorType={"ghost"}
            onClick={() => {
              setFilter({
                period: defaultTimePeriod,
                from: null,
                to: null,
              });
              setFilterByUser([]);
              setFilterByContact([]);
              setFilterByStatus([]);
              setFilterByLabel([]);
              setSearchClients?.("");
            }}
            label={"Clear all"}
            extraClasses={"normal-case whitespace-nowrap"}
          />
        </div>
      )}
    </div>
  );
};
